import {isBlank, isEmpty, isFunction, isNotEmpty} from "../util/type-utils.js";
import {executeOnLoad} from "../util/event-utils.js";


const hashRegex = /([a-z0-9_-]+)\/([^\/;]+);?/ig;

/**
 * @typedef HashParameter
 * @param {String} name
 * @param  {String} value
 */

/**
 * Class to handle site navigation via the URL hash (#) parameter.
 */
class UrlParameterHandler {

    /**
     * @type {HashParameter[]}
     *
     * Array to store all current url parameters
     */
    #hashValues = [];

    /**
     * Object to store all the handlers that subscribed via UrlParameterHandler#subscribe
     *
     * Paran Value is the key, and the value is an array of handlers
     */
    #handlers = {};


    constructor() {


        executeOnLoad(e => {
            this.#updateHashValuesFromURL();
        });


        window.addEventListener('popstate', (e) => {
            const newState = e.state;

            this.#updateHashValuesFromURL(newState);


        });

    }

    /**
     * Takes the current hash and updates the internal array. First empties the parameter array and notifies all subscribers. Secondly, updates the internal array and notifies subscribers again with the new values
     *
     * @param {String|null|undefined} hash Hash to update the url values from. If null|undefined, will read hash from window.url
     */
    #updateHashValuesFromURL(hash) {

        hash = hash || new URL(window.location).hash;


        /**
         * empty internal array and notify subscribers
         */
        const bckHashValues = this.#hashValues;
        this.#hashValues = [];
        for (const hashValue of bckHashValues) {
            hashValue.value = null;
            this.#notifySubscribers(hashValue);
        }

        if (isBlank(hash)) {
            return;
        }

        /**
         * parse values from hash; set values to internal array
         */
        for (const match of hash.matchAll(hashRegex)) {
            const paramName = match[1].toLowerCase(),
                paramValue = match[2];

            const hashParameterObject = {
                name: paramName,
                value: paramValue
            };

            this.#hashValues.push(hashParameterObject);

            this.#notifySubscribers(hashParameterObject);
        }
    }


    /**
     * Update the current url shown in browser to the one reflected by #hashValues
     *
     * @param fireChangeEvent {Boolean}
     * @param pushNewState {Boolean}
     */
    #updateURLFromHashValues(fireChangeEvent, pushNewState = false) {
        const url = new URL(window.location);

        let newHash = '';

        for (const entry of this.#hashValues) {
            newHash += `${entry.name}/${entry.value};`;
        }

        url.hash = newHash;

        if (pushNewState) {
            window.history.pushState(newHash, null, url.toString());
        } else {
            history.replaceState(null, null, url.toString());
        }
    }


    /**
     * Notify all subscribers that have subscribed that have subscribed to the param name (hashParameterObject.name)
     *
     * @param {HashParameter} hashParameterObject
     */
    #notifySubscribers(hashParameterObject) {

        console.debug('Notify Subscribers', hashParameterObject);

        for (const property of Object.keys(this.#handlers)) {

            if (hashParameterObject.name === property) {

                for (const handler of this.#handlers[property]) {
                    if (isFunction(handler)) {
                        handler.call(null, hashParameterObject);
                    }
                }
            }
        }
    }

    /**
     * @callback HashNavigationCallback
     * @param {HashParameter}
     */
    /**
     *
     * @param {String} paramName
     * @param {HashNavigationCallback}handler
     * @returns {HashNavigationCallback}
     *
     * Subscribe to a Hash Navigation event.
     */
    subscribe(paramName, handler) {

        if (isBlank(paramName) || !isFunction(handler)) {
            return null;
        }

        paramName = paramName.toLowerCase();

        if (this.#handlers.hasOwnProperty(paramName)) {
            this.#handlers[paramName].push(handler);
        } else {
            this.#handlers[paramName] = [handler];
        }

        return handler;
    }


    /**
     * Sets the passed parameter to the internal array and updates the url
     *
     * The index defined at what index of the array the new value will be set
     * * If the index is null, the parameter will be inserted at index 0. All subsequent parameters will be removed
     * * If the index is too high (e.G. an index gets skipped, log a warning and do nothing (i.E. only index 0 is set and someone is trying to modify index 2)
     * * If the index is the next higher index, it will be added to the array and subscribers notified
     * * If the index is already set in the internal array, delete all subsequent indexes, notify, and replace the index (and notify again)
     *  *
     *
     * If pushNewState = true, the url will be updated via the Browser history api to enable same-site forward/backwards-navigation
     *
     * @param {HashParameter} hashValue
     * @param {Integer} [index]
     * @param {Boolean} [pushNewState=false]
     */
    setURLHashValue(newHashValue, index, pushNewState = false) {
        if (isNotEmpty(newHashValue) && isEmpty(newHashValue['name']) && isEmpty(newHashValue['value'])) {
            return;
        }


        if (isEmpty(index) || Number.isNaN(index)) {
            index = 0;
        }

        /**
         * If the index passed is bigger than the next allowed index (i.E., an element got skipped), return and log warning
         *
         * i.E.: Nested Element with index 0 is visible and something on index 2 or more gets shown.
         * Disallow this cuz index 1 is missing
         */
        if ((index) > this.#hashValues.length) {
            console.warn("Invalid action: Invalid index passed for setURLHashValue", index);
            return;
        }

        /**
         * Is index is next free index and not empty, add to array and notify.
         */
        if (index === this.#hashValues.length) {
            if (isEmpty(newHashValue)) {
                console.warn("Invalid action: Cannot push null as new index");
                return;
            }
            this.#hashValues.push(newHashValue);
            this.#notifySubscribers(newHashValue);
        } else {

            // do nothing if nothing changes
            const oldValue = this.#hashValues[index];
            if (isEmpty(oldValue) && isEmpty(newHashValue)) {
                return;
            }

            if (isNotEmpty(oldValue) && isNotEmpty(newHashValue) && oldValue.name === newHashValue.name && oldValue.value === newHashValue.value) {
                return;
            }

            // Delete all following index values
            for (let i = 0; i < this.#hashValues.length; i++) {
                if (i > index) {
                    let indexValue = this.#hashValues[i];
                    indexValue.value = null;
                    this.#notifySubscribers(indexValue);
                }
            }
            this.#hashValues = [].slice.call(this.#hashValues, 0, (index + 1));

            if (isEmpty(newHashValue)) {
                let poppedValue = this.#hashValues.pop();
                poppedValue.value = null;
                this.#notifySubscribers(poppedValue);
            } else {
                this.#hashValues[index] = newHashValue;
                this.#notifySubscribers(newHashValue);
            }
        }


        this.#updateURLFromHashValues(false, pushNewState);

    }

}

/**
 * Export as singleton
 */
export default new UrlParameterHandler();